import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Home, FileText, BookOpen, MessageSquare, Users, LogOut, Menu, Activity, Sun, Moon, ChevronLeft, ChevronRight, Contact, ClipboardList, PenLine, BookUser } from 'lucide-react';
import Notifications from '@/components/Notifications';
import SiteSwitcher from '@/components/SiteSwitcher';
import LegalFooter from '@/components/LegalFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Libellés corrects des rôles (un simple remplacement de "_" par un espace
// donnerait "chef poste" au lieu de "Chef de Poste")
const ROLE_LABELS = {
  admin: 'Administrateur',
  chef_poste: 'Chef de Poste',
  agent: 'Agent',
  site: 'Site',
  client: 'Client'
};

const Layout = ({ children, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === 'true');
  const [avatarPhotoUrl, setAvatarPhotoUrl] = useState(null);

  // Charge la photo de profil de l'utilisateur connecté, pour l'afficher à la
  // place de l'avatar à initiale dans le menu latéral.
  useEffect(() => {
    let objectUrl = null;
    const loadAvatar = async () => {
      if (!user?.id) return;
      const token = localStorage.getItem('token');
      try {
        const profRes = await fetch(`${BACKEND_URL}/api/profile/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (profRes.ok) {
          const prof = await profRes.json();
          if (prof.photo_file_id) {
            const fileRes = await fetch(`${BACKEND_URL}/api/files/${prof.photo_file_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (fileRes.ok) {
              const blob = await fileRes.blob();
              objectUrl = URL.createObjectURL(blob);
              setAvatarPhotoUrl(objectUrl);
            }
          }
        }
      } catch { /* silencieux */ }
    };
    loadAvatar();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [user?.id]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', collapsed ? 'true' : 'false');
  }, [collapsed]);

  const menuItems = [
    { label: 'Tableau de bord', path: '/', icon: Home },
  ];
  const otherItems = [
    { label: 'Main Courante', path: '/main-courante', icon: FileText },
    { label: 'Consignes', path: '/consignes', icon: BookOpen },
  ];

  const canUseMessagerie =
    (user?.permission_overrides?.use_messagerie ?? (user?.role !== 'site')) ||
    (user?.permission_overrides?.use_channel_messagerie ?? true);
  if (canUseMessagerie) {
    otherItems.push({ label: 'Messagerie', path: '/messagerie', icon: MessageSquare });
  }

  otherItems.push({ label: 'Organigramme', path: '/trombinoscope', icon: Contact });

  const canManageReport = user?.permission_overrides?.manage_daily_report ?? ['admin'].includes(user?.role);
  const canViewReport = user?.permission_overrides?.view_daily_report ?? ['admin'].includes(user?.role);
  if (canManageReport || canViewReport) {
    otherItems.push({ label: 'Reporting', path: '/reporting', icon: ClipboardList });
  }

  const canWriteAgentReports = user?.permission_overrides?.write_agent_reports ?? ['admin', 'chef_poste', 'agent'].includes(user?.role);
  const canReadAgentReports = user?.permission_overrides?.read_agent_reports ?? ['admin', 'chef_poste', 'agent'].includes(user?.role);
  if (canWriteAgentReports || canReadAgentReports) {
    otherItems.push({ label: 'Comptes rendus', path: '/comptes-rendus', icon: PenLine });
  }

  const canViewDirectory = user?.permission_overrides?.view_directory ?? true;
  if (canViewDirectory) {
    otherItems.push({ label: 'Annuaire', path: '/annuaire', icon: BookUser });
  }

  const hasAdminPagePermission = (keys) =>
    user?.role === 'admin' || keys.some(k => user?.permission_overrides?.[k] === true);

  // Tri alphabétique de tous les accès sauf "Tableau de bord" (toujours en
  // tête) et "Administration"/"Historique" (toujours à la fin, dans cet
  // ordre), chacun séparé par un trait visuel.
  otherItems.sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  if (otherItems.length > 0) otherItems[0].separatorBefore = true;
  menuItems.push(...otherItems);

  if (hasAdminPagePermission(['manage_sites', 'manage_users', 'approve_accounts', 'view_activity_history'])) {
    menuItems.push({ label: 'Administration', path: '/admin', icon: Users, separatorBefore: true });
  }

  if (hasAdminPagePermission(['view_activity_history'])) {
    menuItems.push({ label: 'Historique', path: '/activity', icon: Activity });
  }

  const NavLinks = ({ mobile = false, iconOnly = false }) => (
    <nav className={`${mobile ? 'flex flex-col space-y-2' : 'flex flex-col space-y-1'}`}>
      {menuItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        return (
          <React.Fragment key={item.path}>
            {item.separatorBefore && (
              <div className={`my-2 border-t ${mobile ? 'border-slate-200 dark:border-slate-700' : 'border-white/15'}`} />
            )}
            <Button
            variant={isActive ? 'default' : 'ghost'}
            title={iconOnly ? item.label : undefined}
            className={`${mobile || !iconOnly ? 'w-full justify-start' : 'w-full justify-center'} ${
              isActive
                ? 'bg-gradient-to-r from-indigo-600 to-purple-700 text-white hover:from-indigo-600 hover:to-purple-700'
                : mobile
                ? ''
                : 'text-white hover:bg-white/10 hover:text-white'
            }`}
            onClick={() => {
              navigate(item.path);
              if (mobile) setMobileMenuOpen(false);
            }}
            data-testid={`nav-${item.path === '/' ? 'dashboard' : item.path.substring(1)}`}
          >
            <Icon className={iconOnly && !mobile ? 'h-5 w-5' : 'h-4 w-4 mr-2'} />
            {(!iconOnly || mobile) && item.label}
          </Button>
          </React.Fragment>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 transition-colors md:flex">

      {/* Barre mobile (écrans étroits) */}
      <header className="md:hidden bg-black sticky top-0 z-50 border-b border-indigo-900/40 shadow-lg">
        <div className="px-4 py-2 flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo-action-securite.png" alt="Action Sécurité" className="h-10 object-contain" />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDarkMode(!darkMode)}
              className="text-white hover:bg-white/10 hover:text-white"
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Notifications user={user} />
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" data-testid="mobile-menu-btn" className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <div className="flex flex-col h-full">
                  <button
                    type="button"
                    onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
                    className="mb-6 text-left w-full rounded-lg -mx-1.5 px-1.5 py-1 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <p className="font-medium text-gray-800 dark:text-slate-100">{user?.name}</p>
                    <p className="text-sm text-gray-600 dark:text-slate-400">{ROLE_LABELS[user?.role] || user?.role}</p>
                  </button>
                  <div className="mb-4 empty:hidden">
                    <SiteSwitcher user={user} dark={false} />
                  </div>
                  <NavLinks mobile />
                  <div className="mt-auto pt-4">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        onLogout();
                        setMobileMenuOpen(false);
                      }}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Déconnexion
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Volet latéral (écrans larges) - repliable */}
      <aside
        className={`hidden md:flex md:flex-col bg-black border-r border-indigo-900/40 shadow-lg sticky top-0 h-screen transition-all duration-200 ${
          collapsed ? 'md:w-[76px]' : 'md:w-64'
        }`}
      >
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} px-3 py-4 border-b border-white/10`}>
          {!collapsed && (
            <div className="flex items-center cursor-pointer min-w-0" onClick={() => navigate('/')}>
              <img src="/logo-action-securite.png" alt="Action Sécurité" className="h-10 object-contain" />
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-white hover:bg-white/10 hover:text-white shrink-0"
            title={collapsed ? 'Déplier le menu' : 'Replier le menu'}
            data-testid="sidebar-toggle-btn"
          >
            {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          </Button>
        </div>

        <div className="px-3 pt-3 empty:hidden">
          <SiteSwitcher user={user} iconOnly={collapsed} />
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-4">
          <NavLinks iconOnly={collapsed} />
        </div>

        <div className={`px-3 py-2 flex ${collapsed ? 'flex-col items-center gap-2' : 'items-center justify-between'} border-t border-white/10`}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDarkMode(!darkMode)}
            title={darkMode ? 'Passer au thème clair' : 'Passer au thème sombre'}
            data-testid="theme-toggle-btn"
            className="text-white hover:bg-white/10 hover:text-white"
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          <Notifications user={user} />
        </div>

        <div className="px-3 py-3 border-t border-white/10">
          {!collapsed && (
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="flex items-center gap-3 mb-3 w-full text-left rounded-lg px-1.5 py-1 -mx-1.5 hover:bg-white/10 transition-colors"
              data-testid="nav-profile-shortcut"
            >
              {avatarPhotoUrl ? (
                <img
                  src={avatarPhotoUrl}
                  alt={user?.name}
                  className="w-9 h-9 rounded-full object-cover shadow-lg shrink-0 border border-white/20"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-700 flex items-center justify-center text-white font-bold text-sm shadow-lg shrink-0">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                <p className="text-xs text-indigo-200 truncate">{ROLE_LABELS[user?.role] || user?.role}</p>
              </div>
            </button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onLogout}
            className={`bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white ${collapsed ? 'w-full justify-center px-0' : 'w-full justify-start'}`}
            data-testid="logout-btn"
            title={collapsed ? 'Déconnexion' : undefined}
          >
            <LogOut className={collapsed ? 'h-4 w-4' : 'h-4 w-4 mr-2'} />
            {!collapsed && 'Déconnexion'}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 px-4 py-8 md:px-8">
        {children}
        <LegalFooter className="text-slate-500 dark:text-slate-400" />
      </main>
    </div>
  );
};

export default Layout;
